import {
    Divider,
    FormControlLabel,
    FormLabel,
    Radio,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import { FormLayout } from '../../components/FormLayout';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Button } from '../../components/Button';
import { useApiCall } from '../../lib/useApiCall';
import { api } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { pathHandler } from '../../lib/pathHandler';
import { optinStatusType } from './types';
import { OPTIN_TEXTS } from './constants';

const TEXT_WIDTH = 100;

function DroitImageForm() {
    const [signatureFile, setSignatureFile] = useState<File | null>(null);
    const [formTextData, setTextFormData] = useState({
        firstName: '',
        lastName: '',
        birthDate: '',
        place: '',
        date: '',
    });
    const navigate = useNavigate();
    const createDroitImageEntryApiCall = useApiCall({
        apiCall: api.createDroitImageEntry,
        onSuccess: (data) => {
            navigate(
                pathHandler.getRoutePath('DROIT_IMAGE_PREVIEW', {
                    droitImageId: data.id,
                }),
            );
        },
    });
    const todayDate = computeTodayDate();
    const [optinStatus, setOptinStatus] = useState<optinStatusType | undefined>();
    return (
        <FormLayout
            handleSubmit={handleSubmit}
            title="FORMULAIRE DROIT À L'IMAGE"
            subtitle="Scolarité à l'ésaj"
        >
            <InitialTextContainer>
                <Row>
                    <LeftContainer>
                        <Typography>Je soussigné :</Typography>
                    </LeftContainer>
                    <TextField
                        required
                        variant="standard"
                        name="firstName"
                        label="Prénom"
                        onChange={handleFormChange}
                    />
                    <TextField
                        required
                        variant="standard"
                        name="lastName"
                        label="Nom"
                        onChange={handleFormChange}
                    />
                </Row>
                <Row>
                    <LeftContainer>
                        <Typography>Né(e) le :</Typography>
                    </LeftContainer>
                    <TextField
                        required
                        variant="standard"
                        name="birthDate"
                        type="date"
                        onChange={handleFormChange}
                    />
                    ,
                </Row>
            </InitialTextContainer>
            <MainContentContainer>
                <FormControlLabel
                    control={<Radio checked={optinStatus === 'accepted'} />}
                    onChange={(_, checked) => {
                        if (checked) {
                            setOptinStatus('accepted');
                        }
                    }}
                    label={<Typography>{OPTIN_TEXTS.accepted.radioButtonLabel}</Typography>}
                    name="opt-in"
                />
                {OPTIN_TEXTS.accepted.paragraphs.map((paragraph, index) => (
                    <Paragraph key={`paragraph-${index}`}>{paragraph}</Paragraph>
                ))}

                <Divider />
                <FormControlLabel
                    control={<Radio checked={optinStatus === 'refused'} />}
                    onChange={(_, checked) => {
                        if (checked) {
                            setOptinStatus('refused');
                        }
                    }}
                    label={<Typography>{OPTIN_TEXTS.refused.radioButtonLabel}</Typography>}
                    name="hasOptedOut"
                />
            </MainContentContainer>

            <SignatureElementsContainer>
                <Row>
                    <Typography>Fait à :</Typography>
                    <TextField
                        required
                        variant="standard"
                        name="place"
                        label="Lieu"
                        onChange={handleFormChange}
                        placeholder="Lieu"
                    />
                </Row>
                <Row>
                    <Typography>Le :</Typography>
                    <TextField variant="standard" name="date" disabled value={todayDate} />
                </Row>
                <Row>
                    <SignatureFileLabel required htmlFor="signatureFileInput">
                        Téléverser ma signature (.png / .jpg / .jpeg, max 1 Mo):
                    </SignatureFileLabel>
                    <input
                        required
                        id="signatureFileInput"
                        type="file"
                        onChange={handleFileChange}
                        accept=".png,.jpg,.jpeg"
                    />
                </Row>
            </SignatureElementsContainer>
            <Button
                loadingText="Envoi du formulaire en cours... Cela peut prendre quelques secondes."
                isLoading={createDroitImageEntryApiCall.isLoading}
                fullWidth
                disabled={optinStatus === undefined}
                type="submit"
                variant="contained"
                color="primary"
            >
                ENVOYER LE FORMULAIRE
            </Button>
        </FormLayout>
    );

    function computeTodayDate() {
        const now = new Date();
        return now.toLocaleDateString();
    }

    function handleFormChange(event: ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        setTextFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (optinStatus === undefined) {
            alert("Veuillez sélectionner une option pour le droit à l'image.");
            return;
        }
        if (!signatureFile) {
            alert('Veuillez téléverser votre signature.');
            return;
        }
        const formData = new FormData();
        formData.append('file', signatureFile);
        formData.append(
            'data',
            JSON.stringify({
                ...formTextData,
                optinStatus,
            }),
        );
        createDroitImageEntryApiCall.perform(formData);
    }

    function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.files) {
            setSignatureFile(e.target.files[0]);
        }
    }
}

const InitialTextContainer = styled('div')(({ theme }) => ({}));
const MainContentContainer = styled('div')(({ theme }) => ({ paddingLeft: theme.spacing(2) }));
const Row = styled('div')(({ theme }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
}));
const Paragraph = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}));

const LeftContainer = styled('div')(({ theme }) => ({
    width: TEXT_WIDTH,
}));

const SignatureFileLabel = styled(FormLabel)(({ theme }) => ({ marginRight: theme.spacing(1) }));

const SignatureElementsContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
}));
export { DroitImageForm };
